if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getFundraiserContext() {
        const context = {
          campaignId: '',
          organizationSlug: '',
          sellerSlug: '',
          returnUrl: ''
        };

        const readStorageValue = (keys) => {
          for (const key of keys) {
            try {
              const value = window.localStorage.getItem(key);

              if (value && value !== '#' && value !== 'null' && value !== 'undefined') {
                return value;
              }
            } catch (error) {
              // Ignore localStorage access errors.
            }
          }

          return '';
        };

        try {
          const url = new URL(window.location.href);

          context.campaignId =
            url.searchParams.get('campaign_id') ||
            url.searchParams.get('campaign') ||
            url.searchParams.get('tsg_campaign_id') ||
            '';

          context.organizationSlug =
            url.searchParams.get('fundraiser_org') ||
            url.searchParams.get('org') ||
            url.searchParams.get('organization') ||
            '';

          context.sellerSlug =
            url.searchParams.get('fundraiser_seller') ||
            url.searchParams.get('seller') ||
            url.searchParams.get('participant') ||
            '';

          context.returnUrl =
            url.searchParams.get('fundraiser_return_url') ||
            url.searchParams.get('return_url') ||
            '';
        } catch (error) {
          // Ignore URL parsing errors.
        }

        context.campaignId =
          context.campaignId ||
          readStorageValue([
            'campaign_id',
            'tsg_campaign_id',
            'fundraiser_campaign_id',
            '_campaign_id'
          ]);

        context.organizationSlug =
          context.organizationSlug ||
          readStorageValue([
            'fundraiser_org',
            'organization_slug',
            'tsg_organization_slug',
            '_fundraiser_org'
          ]);

        context.sellerSlug =
          context.sellerSlug ||
          readStorageValue([
            'fundraiser_seller',
            'seller_slug',
            'tsg_seller_slug',
            '_fundraiser_seller'
          ]);

        context.returnUrl =
          context.returnUrl ||
          readStorageValue([
            'fundraiser_return_url',
            '_fundraiser_return_url',
            'tsg_fundraiser_return_url',
            'seller_url',
            'fundraiser_url',
            'tsg_seller_url',
            'tsg_fundraiser_url'
          ]);

        if (!context.returnUrl && (context.campaignId || context.organizationSlug || context.sellerSlug)) {
          context.returnUrl = window.location.href;
        }

        return context;
      }

      persistFundraiserContext(context) {
        if (!context) return;

        const valuesToStore = [
          ['campaign_id', context.campaignId],
          ['tsg_campaign_id', context.campaignId],
          ['fundraiser_org', context.organizationSlug],
          ['tsg_organization_slug', context.organizationSlug],
          ['fundraiser_seller', context.sellerSlug],
          ['tsg_seller_slug', context.sellerSlug],
          ['fundraiser_return_url', context.returnUrl],
          ['tsg_fundraiser_return_url', context.returnUrl]
        ];

        valuesToStore.forEach(([key, value]) => {
          if (!value) return;

          try {
            window.localStorage.setItem(key, value);
          } catch (error) {
            // Ignore localStorage access errors.
          }
        });
      }

      appendFundraiserFields(formData, context) {
        if (!context) return;

        if (context.campaignId) {
          formData.set('properties[Campaign ID]', context.campaignId);
          formData.set('attributes[campaign_id]', context.campaignId);
        }

        if (context.organizationSlug) {
          formData.set('properties[Organization]', context.organizationSlug);
          formData.set('attributes[fundraiser_org]', context.organizationSlug);
        }

        if (context.sellerSlug) {
          formData.set('properties[Seller]', context.sellerSlug);
          formData.set('attributes[fundraiser_seller]', context.sellerSlug);
        }

        if (context.returnUrl) {
          formData.set('properties[Fundraiser Page]', context.returnUrl);
          formData.set('attributes[fundraiser_return_url]', context.returnUrl);
        }
      }

      async updateFundraiserCartAttributes(context) {
        if (!context || (!context.campaignId && !context.organizationSlug && !context.sellerSlug)) {
          return;
        }

        const attributes = {};

        if (context.campaignId) attributes.campaign_id = context.campaignId;
        if (context.organizationSlug) attributes.fundraiser_org = context.organizationSlug;
        if (context.sellerSlug) attributes.fundraiser_seller = context.sellerSlug;
        if (context.returnUrl) attributes.fundraiser_return_url = context.returnUrl;

        try {
          await fetch(`${routes.cart_update_url}`, {
            ...fetchConfig('javascript'),
            body: JSON.stringify({ attributes })
          });
        } catch (error) {
          console.warn('Fundraiser cart attributes could not be updated.', error);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const fundraiserContext = this.getFundraiserContext();
        this.persistFundraiserContext(fundraiserContext);

        const formData = new FormData(this.form);
        this.appendFundraiserFields(formData, fundraiserContext);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              await this.updateFundraiserCartAttributes(fundraiserContext);
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            await this.updateFundraiserCartAttributes(fundraiserContext);

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
